/**
 * Start mission: from a backlog item (creates the mission), from scratch, or another session for
 * an existing mission. Pick project, machine, checkout or new worktree, mode, model and budget;
 * the prompt is built from the goal and the item, previewed before it is sent.
 *
 * Layout (missions vision), prompt-first like New session: what it will do (the item and its
 * criteria count, then the goal), Project and Machine, then Options folded behind one line that
 * says what they are set to (checkout or worktree, mode, model, permissions, budget). The options
 * and whether Options was open are remembered in this browser.
 *
 * Several items (`target.items`, from the backlog's bulk bar) start one mission each with one
 * choice of machine and options: each goal comes from its item, each runs in a new worktree of
 * its own, and the dialog lists the items with their criteria counts.
 */
import { ChevronDown, ChevronRight, FolderGit2, GitBranchPlus, MonitorPlay, Paperclip, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { KeyboardEvent } from 'react';
import { attachmentPromptSection, buildMissionPrompt, promptLinksFor, resolveAttachmentRefs, sequenceCandidates } from '@orgus/protocol';
import { useMissionBundle } from '../attachments/data.ts';
import type { BacklogItem, Checkout, Id, ItemLink, Mission, MissionLaunch } from '@orgus/protocol';
import { Button, Checkbox, Dialog, ErrorState, Input, ProjectMark, Pressable, Segmented, Select, Switch, Textarea, Tooltip, cn, formatMoney, hasMod, plural, shortPath, toast } from '@orgus/ui';
import { startSequence } from '../requirements/data.ts';
import { KeyLabel } from '../requirements/parts.tsx';
import { KindIcon } from './parts.tsx';
import type { SelectOption } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus, useMachines, useProjects } from '../../lib/store.ts';
import { PERMISSION_MODES, modelOptionLabel, permissionLabel, provenModels } from '../sessions/labels.ts';
import { enqueue, useSchedulerState } from '../scheduler/data.ts';
import { createMission, startMission, updateMission } from './data.ts';
import { missionAccountOptions, useAllLaunchAccountChoices } from '../launch-accounts/data.ts';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import { branchFromTitle, budgetHint, goalFromItem, missionsSlice, parseBudget, startFormErrors, startStepText } from './model.ts';
import type { StartForm } from './model.ts';

export interface StartTarget {
  /** Start a new mission for this item. */
  item?: BacklogItem | null;
  /** Start another session for this mission. */
  mission?: Mission | null;
  /** A new mission without an item, in this project. */
  projectId?: Id | null;
  /** A title and goal to start from (a stuck landing's Needs you row, ORGU-83); the person edits them. */
  draft?: { title?: string; goal?: string } | null;
  /** Start one mission for each of these items (one project), side by side, each in its own worktree. */
  items?: readonly BacklogItem[] | null;
  /** Run this epic's (or feature's) open children one mission after another. */
  sequence?: BacklogItem | null;
  /**
   * Retry: the start to repeat (machine, checkout, mode, model), with `mission` for another
   * session of the same mission or `item` for a new mission on it. A model the last session
   * could not use is not repeated.
   */
  retryFrom?: { launch: MissionLaunch | null; failedOnModel?: boolean } | null;
}

/** The options a person set last time (mode, model, permissions, where) and whether Options was open: remembered in this browser. */
interface Remembered {
  open?: boolean;
  mode?: 'headless' | 'terminal';
  model?: string;
  permissionMode?: string;
  where?: 'checkout' | 'worktree';
}
const OPTIONS_KEY = 'orgus.startMission.options';
function recall(): Remembered {
  try {
    const v = JSON.parse(localStorage.getItem(OPTIONS_KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Remembered) : {};
  } catch {
    return {};
  }
}
function remember(patch: Remembered) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify({ ...recall(), ...patch }));
  } catch {
    // Private windows and blocked storage: the dialog simply starts from its defaults next time.
  }
}

/** The worktree branch of one of several missions started together: its key and title, so each is distinct. */
export function manyBranch(it: Pick<BacklogItem, 'key' | 'title'>): string {
  return branchFromTitle(`${it.key} ${it.title}`);
}

function checkoutLabel(c: Checkout): string {
  const dirty = c.dirty ? `, ${c.dirty} changed` : '';
  return `${c.branch ?? 'Detached'} in ${shortPath(c.path, 44)}${c.kind === 'worktree' ? ' (worktree)' : ''}${dirty}`;
}

export function StartMissionDialog({
  open,
  onOpenChange,
  target,
  items,
  links = [],
  onStarted,
  onSequenceStarted,
  onManyStarted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: StartTarget;
  items: readonly BacklogItem[];
  links?: readonly ItemLink[];
  onStarted: (missionId: Id) => void;
  onSequenceStarted?: (rootId: Id) => void;
  /** Several missions started together: their ids. */
  onManyStarted?: (missionIds: Id[]) => void;
}) {
  const projects = useProjects();
  const machines = useMachines();
  const allCheckouts = useOrgus((s) => s.checkouts);
  const sessions = useOrgus((s) => s.sessions);
  const costsOn = useOrgus((s) => !!(s.ext.costs as { sessions?: unknown } | undefined)?.sessions);
  const scheduler = useSchedulerState().state;
  const existing = target.mission ?? null;
  const seqRoot = target.sequence ?? null;
  const picked = target.items ?? null;
  const many = !existing && !seqRoot && picked && picked.length > 1 ? picked : null;
  const retry = target.retryFrom ?? null;
  const item = existing ? (existing.itemId ? items.find((i) => i.id === existing.itemId) ?? null : null) : (target.item ?? seqRoot ?? many?.[0] ?? null);
  const creating = !existing && !seqRoot && !many;
  const steps = useMemo(() => (seqRoot ? sequenceCandidates(seqRoot.id, [...items]) : many ? [...many] : []), [seqRoot, many, items]);
  const [chosen, setChosen] = useState<Set<Id>>(new Set());
  useEffect(() => {
    if (open) setChosen(new Set(steps.map((s) => s.id)));
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [form, setForm] = useState<StartForm>({ title: '', goal: '', projectId: '', machineId: '', where: 'checkout', checkoutId: '', branch: '', budget: '', note: '' });
  const [branchTouched, setBranchTouched] = useState(false);
  // QA-63: opt in per mission; off unless this mission already stops at its budget.
  const [hardStop, setHardStop] = useState(false);
  const [mode, setMode] = useState<'headless' | 'terminal'>('headless');
  const [model, setModel] = useState('');
  const [permissionMode, setPermissionMode] = useState('default');
  // The launch account (ADR 0071): '' leaves it to the project's default, else the machine.
  const [launchAccountId, setLaunchAccountId] = useState('');
  // ADR 0078: every agent's accounts; the account decides the agent of a mission nobody pinned.
  const accountChoices = useAllLaunchAccountChoices();
  const [showPrompt, setShowPrompt] = useState(false);
  const [optionsOpen, setOptionsOpenState] = useState(false);
  const setOptionsOpen = (v: boolean) => {
    setOptionsOpenState(v);
    remember({ open: v });
  };
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  /** A mission this dialog already created (the start failed): retries reuse it. */
  const [createdId, setCreatedId] = useState<Id | null>(null);
  /** Where Start has got to while it runs: saving the mission, then the hub's start. */
  const [phase, setPhase] = useState<'saving' | 'starting' | null>(null);
  const startingId = existing?.id ?? createdId;
  const hubStep = useOrgus((s) => (startingId ? missionsSlice(s.ext)?.starting?.find((x) => x.missionId === startingId)?.step : undefined));
  const titleRef = useRef<HTMLInputElement>(null);
  const goalRef = useRef<HTMLTextAreaElement>(null);
  const set = (patch: Partial<StartForm>) => setForm((f) => ({ ...f, ...patch }));

  const checkouts = useMemo(() => Object.values(allCheckouts), [allCheckouts]);
  const machineById = useMemo(() => new Map(machines.map((m) => [m.id, m])), [machines]);

  const machinesFor = (pid: string) => {
    const ids = new Set(checkouts.filter((c) => c.projectId === pid).map((c) => c.machineId));
    return machines.filter((m) => ids.has(m.id));
  };
  const checkoutsFor = (pid: string, mid: string) =>
    checkouts.filter((c) => c.projectId === pid && c.machineId === mid).sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path));
  const pickMachine = (pid: string, preferred?: string): string => {
    const list = machinesFor(pid);
    const ok = (id?: string) => !!id && list.some((m) => m.id === id && m.online);
    if (ok(preferred)) return preferred!;
    const recent = Object.values(sessions)
      .filter((s) => s.projectId === pid && ok(s.machineId))
      .sort((a, b) => b.lastActivity - a.lastActivity)[0]?.machineId;
    return (ok(recent) ? recent : list.find((m) => m.online)?.id) ?? list[0]?.id ?? '';
  };
  const firstCheckout = (pid: string, mid: string) => {
    const list = checkoutsFor(pid, mid);
    return (list.find((c) => c.kind === 'main') ?? list[0])?.id ?? '';
  };

  useEffect(() => {
    if (!open) return;
    const pid = existing?.projectId ?? item?.projectId ?? target.projectId ?? projects[0]?.id ?? '';
    const last = retry?.launch ?? null;
    const mid = pickMachine(pid, last?.machineId ?? existing?.placement.machineId);
    const sameCheckout = last?.checkoutId && last.machineId === mid && checkoutsFor(pid, mid).some((c) => c.id === last.checkoutId) ? last.checkoutId : null;
    const title = existing?.title ?? item?.title ?? target.draft?.title ?? '';
    const r = recall();
    setOptionsOpenState(!!r.open);
    setForm({
      title,
      goal: existing?.goal ?? (item ? goalFromItem(item) : (target.draft?.goal ?? '')),
      projectId: pid,
      machineId: mid,
      // Several missions side by side never share a checkout: each gets a worktree of its own. A
      // single mission takes a worktree too unless the person chose the checkout last time: its
      // branch is what Check, Review and Land work on (ADR 0086).
      where: many || (!last && r.where !== 'checkout') ? 'worktree' : 'checkout',
      checkoutId: sameCheckout ?? firstCheckout(pid, mid),
      branch: '',
      budget: existing?.budget != null ? String(existing.budget) : '',
      note: '',
    });
    setBranchTouched(false);
    setHardStop(!!existing?.budgetHardStop);
    setMode(last?.mode ?? r.mode ?? 'headless');
    // A model the account could not use is never offered again as the default.
    setModel(retry?.failedOnModel ? '' : (last?.model ?? existing?.model ?? r.model ?? ''));
    setPermissionMode(r.permissionMode ?? 'default');
    setShowPrompt(false);
    setShowErrors(false);
    setError(null);
    setBusy(false);
    setPhase(null);
    setCreatedId(null);
    // Only on open: live updates must not reset what the person typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const branch = many ? manyBranch(many[0]!) : branchTouched ? form.branch : form.branch || branchFromTitle(form.title);
  const formNow: StartForm = { ...form, branch };
  const machineCheckouts = checkoutsFor(form.projectId, form.machineId);
  const errors = startFormErrors(formNow, {
    creating,
    machineOnline: (id) => machineById.get(id)?.online ?? false,
    machineName: (id) => machineById.get(id)?.name,
    checkouts: machineCheckouts,
  });
  const valid = Object.keys(errors).length === 0;
  const err = (k: keyof typeof errors) => (showErrors ? errors[k] : undefined);

  // Claude Code's aliases (opus, sonnet, haiku), then models sessions actually answered with.
  const models = useMemo(
    () => [{ value: '', label: 'Machine default' }, ...provenModels(Object.values(sessions), 'claude-code', retry?.failedOnModel ? null : (retry?.launch?.model ?? existing?.model ?? null)).map((m) => ({ value: m, label: modelOptionLabel(m) }))],
    // The list is fixed while the dialog is open, so a pick never disappears under the pointer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, open],
  );

  const bundle = useMissionBundle(item?.id ?? existing?.itemId, existing?.id ?? createdId);
  const folder = `<agent home>/attachments/${existing?.id ?? createdId ?? 'msn_new'}`;
  const filesSection = attachmentPromptSection({ files: bundle.map((a) => ({ id: a.id, name: a.name, mime: a.mime, size: a.size, path: `${folder}/${a.name}` })), failed: [] });
  const itemMap = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const prompt = resolveAttachmentRefs(
    buildMissionPrompt(
      { id: existing?.id ?? createdId ?? 'msn_new', title: form.title || 'Untitled mission', goal: form.goal || '(goal)' },
      item,
      [form.note.trim(), filesSection].filter(Boolean).join('\n\n'),
      item ? promptLinksFor(item, itemMap, links) : [],
      { worktree: !!many || form.where === 'worktree' },
    ),
    new Map(bundle.map((a) => [a.id, `${folder}/${a.name}`])),
  );

  const submitSequence = async () => {
    setShowErrors(true);
    const order = steps.filter((s) => chosen.has(s.id)).map((s) => s.id);
    if (!seqRoot || busy || errors.machine || errors.checkout || errors.project || !order.length) return;
    setBusy(true);
    setError(null);
    try {
      await startSequence({ itemId: seqRoot.id, itemIds: order, start: { machineId: form.machineId, checkoutId: form.checkoutId, mode: 'headless', ...(model ? { model } : {}), ...(permissionMode !== 'default' ? { permissionMode } : {}) } });
      toast.success('Sequence started', `${order.length} ${order.length === 1 ? 'mission' : 'missions'}, one after another, on ${machineById.get(form.machineId)?.name ?? 'the machine'}.`);
      remember({ model, permissionMode });
      onSequenceStarted?.(seqRoot.id);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  /** One mission per chosen item, started one after another on the chosen machine, each in its own worktree. */
  const submitMany = async (queue: boolean) => {
    setShowErrors(true);
    const list = steps.filter((s) => chosen.has(s.id));
    // A queued start needs no machine yet; branches are made per item, so the form's branch is not checked.
    const blocking = errors.project || errors.budget || errors.note || (!queue && (errors.machine || errors.checkout));
    if (!many || busy || !list.length || blocking) return;
    setBusy(true);
    setError(null);
    const budget = parseBudget(form.budget).value;
    const base = machineCheckouts.find((c) => c.kind === 'main') ?? machineCheckouts[0];
    const started: Id[] = [];
    const failed: string[] = [];
    for (const it of list) {
      try {
        const m = await createMission({ projectId: it.projectId, title: `${it.key} ${it.title}`.slice(0, 200), goal: goalFromItem(it), itemId: it.id, budget, budgetHardStop: budget != null && hardStop, model: model || null, ...(queue ? {} : { placement: { machineId: form.machineId } }) });
        const common = { mode, model: model || null, ...(permissionMode !== 'default' ? { permissionMode } : {}), newWorktree: { branch: manyBranch(it) }, ...(form.note.trim() ? { note: form.note.trim() } : {}) };
        if (queue) {
          const e = await enqueue({ missionId: m.id, ...common });
          if (e.state === 'failed') throw new Error(e.message ?? 'The machine did not start it.');
        } else {
          if (!base) throw new Error('No checkout to branch from on this machine.');
          await startMission(m.id, { machineId: form.machineId, checkoutId: base.id, ...common, ...(launchAccountId && mode === 'headless' ? { launchAccountId } : {}), budget, budgetHardStop: budget != null && hardStop });
        }
        started.push(m.id);
      } catch (e) {
        failed.push(`${it.key}: ${toUiError(e).why}`);
      }
    }
    setBusy(false);
    remember({ mode, model, permissionMode });
    if (started.length) toast.success(queue ? `Queued ${plural(started.length, 'mission')}` : `Started ${plural(started.length, 'mission')}`, queue ? 'Each starts on the first machine that fits.' : `On ${machineById.get(form.machineId)?.name ?? 'the machine'}, each in its own worktree.`);
    if (failed.length) {
      // The ones that started stay started; the rest say why, and starting again makes new missions for them.
      setChosen(new Set(list.filter((it) => failed.some((f) => f.startsWith(`${it.key}:`))).map((it) => it.id)));
      setError({ what: `${plural(failed.length, 'mission')} did not start.`, why: failed.slice(0, 3).join(' '), next: started.length ? 'The others are running. Fix the cause, then start the rest again.' : 'Fix the cause, then start them again.' });
      return;
    }
    onManyStarted?.(started);
    onOpenChange(false);
  };

  const submit = async () => {
    if (seqRoot) return submitSequence();
    if (many) return submitMany(false);
    setShowErrors(true);
    if (!valid || busy) {
      if (errors.title) titleRef.current?.focus();
      else if (errors.goal) goalRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    const budget = parseBudget(form.budget).value;
    const base = machineCheckouts.find((c) => c.kind === 'main') ?? machineCheckouts[0];
    const checkoutId = form.where === 'worktree' ? base!.id : form.checkoutId;
    let missionId = existing?.id ?? createdId;
    try {
      if (!missionId) {
        setPhase('saving');
        const m = await createMission({ projectId: form.projectId, title: form.title.trim(), goal: form.goal.trim(), itemId: item?.id ?? null, budget, budgetHardStop: budget != null && hardStop, model: model || null, placement: { machineId: form.machineId } });
        missionId = m.id;
        setCreatedId(m.id);
      }
      setPhase('starting');
      const res = await startMission(missionId, {
        machineId: form.machineId,
        checkoutId,
        mode,
        model: model || null,
        ...(permissionMode !== 'default' ? { permissionMode } : {}),
        ...(form.where === 'worktree' ? { newWorktree: { branch: branch.trim() } } : {}),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
        ...(launchAccountId && mode === 'headless' ? { launchAccountId } : {}),
        budget,
        budgetHardStop: budget != null && hardStop,
      });
      const machine = machineById.get(form.machineId)?.name ?? 'the machine';
      if (res.pending) toast.success('Mission pending', `${machine} is still starting it. The session links here when it appears.`);
      else
        toast.success(
          mode === 'terminal' ? 'Mission started in a terminal' : retry ? 'Mission retried' : 'Mission started',
          res.launch.sessionId || mode === 'headless' ? `Claude is working on ${machine}.` : `Claude Code is open in a new terminal on ${machine}. The session links here when it appears.`,
        );
      remember({ mode, model, permissionMode, where: form.where });
      onStarted(missionId);
      onOpenChange(false);
    } catch (e) {
      const ui = toUiError(e);
      setError(missionId && !existing ? { ...ui, next: `${ui.next} The mission is saved; starting again reuses it.` } : ui);
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  /** Queue instead of picking a machine: the scheduler starts it on the first machine that fits. */
  const queueIt = async () => {
    if (many) return submitMany(true);
    setShowErrors(true);
    const { machine: _m, checkout: _c, ...rest } = errors;
    if (Object.keys(rest).length || busy) return;
    setBusy(true);
    setError(null);
    const budget = parseBudget(form.budget).value;
    let missionId = existing?.id ?? createdId;
    try {
      if (!missionId) {
        const m = await createMission({ projectId: form.projectId, title: form.title.trim(), goal: form.goal.trim(), itemId: item?.id ?? null, budget, budgetHardStop: budget != null && hardStop, model: model || null });
        missionId = m.id;
        setCreatedId(m.id);
      } else if (existing && (budget !== existing.budget || (budget != null && hardStop) !== !!existing.budgetHardStop)) {
        await updateMission(missionId, { budget, budgetHardStop: budget != null && hardStop });
      }
      const e = await enqueue({
        missionId,
        mode,
        model: model || null,
        ...(permissionMode !== 'default' ? { permissionMode } : {}),
        ...(form.where === 'worktree' ? { newWorktree: { branch: branch.trim() } } : {}),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      });
      if (e.state === 'failed') throw new Error(e.message ?? 'The machine did not start it.');
      toast.success(e.state === 'queued' ? 'Mission queued' : 'Mission started', e.message ?? 'It starts on the first machine that fits.');
      remember({ mode, model, permissionMode, where: form.where });
      onStarted(missionId);
      onOpenChange(false);
    } catch (err) {
      const ui = toUiError(err);
      setError(missionId && !existing ? { ...ui, next: `${ui.next} The mission is saved; queuing again reuses it.` } : ui);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && hasMod(e)) {
      e.preventDefault();
      void submit();
    }
  };

  const projectMachines = machinesFor(form.projectId);
  const machineOptions: SelectOption[] = projectMachines.map((m) => ({ value: m.id, label: m.online ? m.name : `${m.name} (offline)`, disabled: !m.online }));
  const stepText = startStepText(phase, hubStep, form.where);
  const title = seqRoot ? `Run ${seqRoot.key} as a sequence` : many ? `Start ${plural(many.length, 'mission')}` : retry ? 'Retry mission' : existing ? 'Start another session' : item ? 'Start mission' : 'New mission';
  const checkout = machineCheckouts.find((c) => c.id === form.checkoutId);
  const budget = parseBudget(form.budget).value;
  // The Options line says where it runs and only what differs from the defaults, so it stays short enough to read on a phone.
  const optionsSummary = [
    seqRoot || many ? null : form.where === 'worktree' ? `New worktree ${branch || ''}`.trim() : checkout ? (checkout.branch ?? 'Detached checkout') : 'Checkout',
    !seqRoot && mode === 'terminal' ? 'in a terminal' : null,
    model ? modelOptionLabel(model) : null,
    permissionMode !== 'default' ? permissionLabel(permissionMode).toLowerCase() : null,
    !seqRoot && budget != null ? `budget ${formatMoney(budget)}${hardStop ? ', stops there' : ''}` : null,
  ]
    .filter(Boolean)
    .join(', ') || 'Defaults';
  // A project the start is tied to (an item's, a mission's) is a fact, not a choice.
  const fixedProject = existing || item ? projects.find((p) => p.id === form.projectId) : undefined;
  const criteria = item?.acceptance.length ?? 0;
  const optionErrors = showErrors && !!(errors.checkout || errors.branch || errors.budget || errors.note);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={seqRoot ? 'Each starts when the one before it is done; a failure stops the sequence.' : many ? 'Each in its own worktree' : existing ? existing.title : undefined}
      size="lg"
      dismissible={!busy}
      initialFocus={() => (creating ? (item ? goalRef.current : titleRef.current) : null)}
      footer={
        <>
          {/* While Start runs, the footer says what it is doing. On a phone the buttons stack full width, the primary on top. The Machine field already says where it runs. */}
          {stepText && (
            <span role="status" className="mr-auto truncate text-caption text-fg-muted max-sm:order-last max-sm:w-full">
              {stepText}…
            </span>
          )}
          <Button variant="ghost" className="max-sm:order-last max-sm:w-full" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {scheduler && (
            <Tooltip content="Starts on the first machine that fits, now or when one frees up">
              <Button variant="secondary" className="max-sm:w-full" onClick={() => void queueIt()} disabled={busy}>
                Queue for any machine
              </Button>
            </Tooltip>
          )}
          <Button variant="primary" className="max-sm:order-first max-sm:w-full" loading={busy} onClick={submit} shortcut={['mod', 'enter']}>
            {seqRoot || many ? `Start ${plural(chosen.size, 'mission')}` : retry ? 'Retry mission' : existing ? 'Start session' : 'Start mission'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6" onKeyDown={onKeyDown}>
        {!machines.length && (
          // First run: a mission needs a machine, so say where to get one instead of empty pickers.
          <div className="flex flex-wrap items-center gap-3 rounded-card border border-border-subtle bg-hover px-3 py-2" role="note">
            <p className="min-w-0 flex-1 text-ui text-fg-muted">No machine is connected yet.</p>
            <Link to="/getting-started?step=machine" className="text-ui font-medium text-accent-text underline-offset-2 hover:underline" onClick={() => onOpenChange(false)}>
              Connect a machine
            </Link>
          </div>
        )}

        {/* What it will do: the item, then the goal that leads the prompt. */}
        {item && !seqRoot && !many && (
          <div className="flex min-w-0 items-center gap-2" aria-label="The item it works">
            <KindIcon kind={item.kind} />
            <KeyLabel value={item.key} />
            <span className="min-w-0 truncate text-ui text-fg">{item.title}</span>
            <span className="ml-auto shrink-0 text-caption text-fg-subtle num">{criteria ? plural(criteria, 'criterion', 'criteria') : 'No criteria'}</span>
          </div>
        )}
        {(seqRoot || many) && (
          <div className="flex flex-col gap-2">
            <span className="text-caption text-fg-subtle">{many ? 'Items' : 'Items, in order'}</span>
            {steps.length ? (
              <ol className="-mx-2 flex max-h-56 flex-col overflow-y-auto">
                {steps.map((s, i) => (
                  <li key={s.id} className="flex h-row items-center gap-3 rounded-control px-2 hover:bg-hover">
                    <Checkbox
                      aria-label={`Include ${s.key}`}
                      checked={chosen.has(s.id)}
                      onCheckedChange={(v) => setChosen((c) => (v ? new Set([...c, s.id]) : new Set([...c].filter((x) => x !== s.id))))}
                    />
                    {/* Side by side has no order: the step number is for a sequence. */}
                    {!many && <span className="w-5 text-caption text-fg-subtle num">{i + 1}</span>}
                    <KindIcon kind={s.kind} />
                    {/* A fixed key column, as in the drawer's Under it list, so every title starts at the same x. */}
                    <KeyLabel value={s.key} className="w-20 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-ui text-fg-muted">{s.title}</span>
                    {many && <span className="shrink-0 text-caption text-fg-subtle num">{s.acceptance.length ? plural(s.acceptance.length, 'criterion', 'criteria') : 'No criteria'}</span>}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-ui text-fg-subtle">Nothing under it is open for a mission</p>
            )}
            {showErrors && !chosen.size && <p className="text-ui text-failed">Choose at least one item.</p>}
          </div>
        )}
        {creating && (
          <div className="flex flex-col gap-4">
            {!item && <Input ref={titleRef} label="Mission" value={form.title} onChange={(e) => set({ title: e.target.value })} error={err('title')} maxLength={200} />}
            <Textarea ref={goalRef} label="Goal" hint="What done looks like" rows={3} value={form.goal} onChange={(e) => set({ goal: e.target.value })} error={err('goal')} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {fixedProject ? (
            <div className="flex min-w-0 flex-col gap-2" role="group" aria-label={`Project: ${fixedProject.name}`}>
              <span className="text-ui font-medium text-fg-muted">Project</span>
              <span className="flex h-control min-w-0 items-center gap-2 text-ui text-fg">
                <ProjectMark projectId={fixedProject.id} name={fixedProject.name} />
                <span className="min-w-0 truncate">{fixedProject.name}</span>
              </span>
            </div>
          ) : (
            <Select
              label="Project"
              value={form.projectId}
              disabled={!!existing || !!item}
              onValueChange={(pid) => {
                const mid = pickMachine(pid, form.machineId);
                set({ projectId: pid, machineId: mid, checkoutId: firstCheckout(pid, mid) });
              }}
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
              placeholder="Choose a project"
              error={err('project')}
            />
          )}
          <Select
            label="Machine"
            value={form.machineId}
            onValueChange={(mid) => set({ machineId: mid, checkoutId: firstCheckout(form.projectId, mid) })}
            options={machineOptions}
            placeholder={projectMachines.length ? 'Choose a machine' : 'No machine has this project'}
            error={showErrors ? errors.machine : form.machineId && !machineById.get(form.machineId)?.online ? errors.machine : undefined}
          />
        </div>

        {/* Everything past the essentials, folded behind one line that says what it is set to (remembered). */}
        <div className="flex flex-col gap-4">
          <Pressable
            onClick={() => setOptionsOpen(!optionsOpen)}
            aria-expanded={optionsOpen || optionErrors}
            className="flex min-w-0 items-center gap-2 self-stretch rounded-control text-left text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            <ChevronRight className={cn('size-3 shrink-0 transition-transform duration-(--duration-fast)', (optionsOpen || optionErrors) && 'rotate-90')} aria-hidden />
            <span className="shrink-0 font-medium text-fg-muted">Options</span>
            {!(optionsOpen || optionErrors) && <span className="min-w-0 truncate">{optionsSummary}</span>}
          </Pressable>
          {(optionsOpen || optionErrors) && (
            <div className="flex flex-col gap-4">
              {creating && item && <Input ref={titleRef} label="Mission title" value={form.title} onChange={(e) => set({ title: e.target.value })} error={err('title')} maxLength={200} />}
              {!seqRoot && !many && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Segmented
                      label="Where"
                      value={form.where}
                      onChange={(where) => set({ where })}
                      options={[
                        { value: 'checkout', label: 'Existing checkout', icon: <FolderGit2 /> },
                        { value: 'worktree', label: 'New worktree', icon: <GitBranchPlus /> },
                      ]}
                    />
                    <Segmented
                      label="Mode"
                      value={mode}
                      onChange={setMode}
                      options={[
                        { value: 'headless', label: 'Headless', icon: <MonitorPlay /> },
                        { value: 'terminal', label: 'In a terminal', icon: <SquareTerminal /> },
                      ]}
                    />
                  </div>
                  {form.where === 'checkout' ? (
                    <Select
                      aria-label="Checkout"
                      value={form.checkoutId}
                      onValueChange={(checkoutId) => set({ checkoutId })}
                      options={machineCheckouts.map((c) => ({ value: c.id, label: checkoutLabel(c) }))}
                      placeholder={machineCheckouts.length ? 'Choose a checkout' : 'No checkout on this machine'}
                      error={err('checkout')}
                    />
                  ) : (
                    <Input
                      aria-label="Branch for the new worktree"
                      value={branch}
                      onChange={(e) => {
                        setBranchTouched(true);
                        set({ branch: e.target.value });
                      }}
                      hint="A new branch from the default branch, in its own folder."
                      error={err('branch') ?? err('checkout')}
                      className="font-mono"
                    />
                  )}
                </div>
              )}
              {many && (
                <Segmented
                  label="Mode"
                  value={mode}
                  onChange={setMode}
                  options={[
                    { value: 'headless', label: 'Headless', icon: <MonitorPlay /> },
                    { value: 'terminal', label: 'In a terminal', icon: <SquareTerminal /> },
                  ]}
                />
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Select label="Model" value={model} onValueChange={setModel} options={models} hint={retry?.failedOnModel ? 'The last model is not available to this account.' : undefined} />
                <Select label="Permissions" value={permissionMode} onValueChange={setPermissionMode} options={PERMISSION_MODES.map((p) => ({ value: p, label: permissionLabel(p) }))} />
                {!seqRoot && <Input label="Budget (USD)" inputMode="decimal" value={form.budget} onChange={(e) => set({ budget: e.target.value })} error={err('budget')} hint={budgetHint(costsOn, budget != null && hardStop)} />}
              </div>
              {/* QA-63: opt in, shown once there is a budget to stop at. */}
              {!seqRoot && budget != null && (
                <label className="flex w-fit items-center gap-2 text-ui text-fg" data-budget-hard-stop>
                  <Switch size="sm" checked={hardStop} onCheckedChange={(v) => setHardStop(v === true)} />
                  Stop the mission at this budget
                </label>
              )}
              {accountChoices.length > 0 && !seqRoot && mode === 'headless' && (
                <Select
                  label="Account"
                  value={launchAccountId}
                  onValueChange={setLaunchAccountId}
                  options={missionAccountOptions(accountChoices, form.projectId).map((o) => ({ value: o.value, label: o.label, ...(o.agent ? { icon: <AgentGlyph kind={o.agent} className="size-3.5" /> } : {}) }))}
                  hint="The account also decides the agent the session runs."
                />
              )}
              {!seqRoot && <Textarea label={many ? 'Note for each session' : 'Note for this session'} hint="Appended to the prompt." rows={2} value={form.note} onChange={(e) => set({ note: e.target.value })} error={err('note')} />}
              {bundle.length > 0 && (
                <p className="flex items-center gap-2 text-caption text-fg-subtle">
                  <Paperclip className="size-3 shrink-0" aria-hidden />
                  {plural(bundle.length, 'attachment')} copied to the machine first
                </p>
              )}
              {!seqRoot && !many && (
                <div className="flex flex-col gap-2">
                  <Pressable className="flex w-auto items-center gap-2 self-start rounded-control text-caption text-fg-subtle hover:text-fg-muted" aria-expanded={showPrompt} onClick={() => setShowPrompt((v) => !v)}>
                    {showPrompt ? <ChevronDown className="size-3" aria-hidden /> : <ChevronRight className="size-3" aria-hidden />}
                    <span className="font-medium text-fg-muted">Prompt Claude receives</span>
                  </Pressable>
                  {showPrompt && <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-card border border-border-subtle px-3 py-2 font-mono text-caption text-fg-muted">{prompt}</pre>}
                </div>
              )}
            </div>
          )}
        </div>
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
